import { inSpeedRun, onboardingIdFor } from '@/lib/speedRun';
import { useCallback, useSyncExternalStore } from "react";
import { useUser } from "@clerk/clerk-react";
import { track } from "@/lib/analytics";
import { isUiMode, resolveUiMode, type UiMode } from "@/lib/uiMode.core";
import { isViewingAsSomeoneElse } from "@/lib/viewAs";

/****************
 * Lite or Pro for the signed-in account (lib/uiMode.core.ts). Kept on the
 * Clerk account (unsafeMetadata.uiMode) so the phone agrees, mirrored in this
 * browser so a switch shows at once and survives a failed write.
 ****************/
const KEY = (id: string) => `copyfi.uiMode.${id}`;
const listeners = new Set<() => void>();
const emit = () => listeners.forEach((l) => l());
const subscribe = (l: () => void) => { listeners.add(l); return () => { listeners.delete(l); }; };

function readLocal(id: string | undefined): UiMode | null {
  if (!id) return null;
  try { const v = localStorage.getItem(KEY(id)); return isUiMode(v) ? v : null; } catch { return null; }
}

export function useUiMode(): { mode: UiMode; setMode: (m: UiMode) => void; ready: boolean } {
  const { user, isLoaded } = useUser();
  const local = useSyncExternalStore(subscribe, () => readLocal(user?.id), () => null);
  const remote = user?.unsafeMetadata?.uiMode;
  // Viewing someone else's account shows Pro: the team needs to see everything.
  // A speed run (lib/speedRun.ts) has its own mode, Lite until switched, never the account's.
  const run = inSpeedRun() && user ? readLocal(onboardingIdFor(user.id)) : null;
  const mode: UiMode = isViewingAsSomeoneElse() ? "pro" : inSpeedRun() ? (run ?? "lite") : resolveUiMode(local ?? remote, user?.createdAt);
  const setMode = useCallback((m: UiMode) => {
    if (!user || isViewingAsSomeoneElse()) return;
    if (inSpeedRun()) { try { localStorage.setItem(KEY(onboardingIdFor(user.id)), m); } catch { /* run only */ } emit(); return; }
    try { localStorage.setItem(KEY(user.id), m); } catch { /* the account copy still holds it */ }
    emit();
    track("ui_mode_switched", m);
    user.update({ unsafeMetadata: { ...user.unsafeMetadata, uiMode: m } }).catch(() => {});
  }, [user]);
  return { mode, setMode, ready: isLoaded };
}
